import type { ComsStore, ComsStorePersist } from '@mfe-4cs/state/ComsStore';
import { Identity } from '@mfe-4cs/types/comsApi';
import { ApiLoadConsentResponse, TefComs } from '@mfe-4cs/types/typings';
import SessionStorage from '@mfe-4cs/util/SessionStorage';

const STORAGE_NAME = 'tef-coms4cs-state';

interface Coms4csSessionStorageItem {
  identity: Identity;
  config: TefComs.ConfigOptions;
  pageStatic: ApiLoadConsentResponse;
  pageDynamic: Record<string, unknown>[];
}

/**
 * Manages the state persistence and retrieval for ComsStore
 */
export class Coms4csState {
  static readonly MAX_UNDO_DEEPNESS = 1;

  /**
   * The key used to store and retrieve state from SessionStorage
   */
  static readonly STORAGE_NAME = STORAGE_NAME;

  /**
   * Reference to the ComsStore instance that this state manager is associated with
   */
  private comsStore: ComsStore;

  /**
   * Creates a new instance of Coms4csState
   *
   * @param comsStore - The ComsStore instance to manage state for
   */
  constructor(comsStore: ComsStore) {
    this.comsStore = comsStore;
  }

  /**
   * Saves the state to SessionStorage or clears it if no state is provided
   *
   * @param state - The state to save, or undefined to clear the state
   */
  private static setState(state?: ComsStorePersist) {
    if (!state) {
      SessionStorage.setItem(STORAGE_NAME);
    } else {
      const item: Coms4csSessionStorageItem = {
        identity: state.identity,
        config: state.config,
        pageStatic: state.page[0],
        pageDynamic: state.page.map((p) => Coms4csState.filterGrantedProperties(p)),
      };

      SessionStorage.setItem(STORAGE_NAME, item);
    }
  }

  /**
   * Filters the properties of an object, keeping only the "granted" property and recursively filtering any nested objects or arrays.
   *
   * @param {Record<string, unknown>} obj - The input object to be filtered.
   * @return {Record<string, unknown>} A new object containing only the "granted" property or nested objects/arrays that contain the "granted" property.
   */
  static filterGrantedProperties(obj: Record<string, unknown>) {
    const result: Record<string, unknown> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(obj)) {
      if (key === 'granted') {
        result[key] = value; // Keep "granted" property
      } else if (Array.isArray(value)) {
        result[key] = value.map((p) => Coms4csState.filterGrantedProperties(p as Record<string, unknown>)); // Keep array property
      } else if (typeof value === 'object') {
        const filteredValue = Coms4csState.filterGrantedProperties(value as Record<string, unknown>);
        if (Object.keys(filteredValue).length > 0) {
          result[key] = filteredValue;
        }
      }
    }
    return result;
  }

  /**
   * Merges the `granted` properties from the dynamic object into the fixed object, preserving the structure
   * of the fixed object and recursively handling nested objects or arrays.
   *
   * @param {Record<string, unknown>} fixed - The base object that acts as the source structure.
   * @param {Record<string, unknown>} dynamic - The object containing dynamic changes to merge into the fixed object.
   * @return {Record<string, unknown>} A new object with the merged properties where `granted` fields take precedence,
   * and arrays or nested objects are merged recursively.
   */
  static mergeGrantedProperties(fixed: Record<string, unknown>, dynamic: Record<string, unknown>) {
    const result = structuredClone(fixed);
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(dynamic)) {
      if (key === 'granted') {
        result[key] = value;
      } else if (Array.isArray(value)) {
        // @ts-expect-error "ok"
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        result[key] = value.map((dyn, ind) => Coms4csState.mergeGrantedProperties(fixed[key][ind], dyn)); // Keep array property
      } else if (typeof value === 'object') {
        // @ts-expect-error "ok"
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        result[key] = Coms4csState.mergeGrantedProperties(fixed[key], value);
      }
    }
    return result;
  }

  /**
   * Retrieves the current state from SessionStorage
   *
   * If no state exists, creates a default state with empty page array and current identity and config
   * If the page property is not an array, converts it to an array with the page as its only element
   *
   * @return {ComsStorePersist} The current state
   */
  getState(): ComsStorePersist {
    const state = SessionStorage.getItem<Coms4csSessionStorageItem & { page: unknown }>(STORAGE_NAME);

    if (!state) {
      return {
        page: [],
        identity: this.comsStore.identity!,
        config: this.comsStore.config!,
      };
    }

    // back compatibility
    if (state.page) {
      if (!Array.isArray(state.page)) {
        return {
          ...state,
          page: [state.page as ApiLoadConsentResponse],
        };
      }
      return state as ComsStorePersist;
    }

    return <ComsStorePersist>{
      identity: state.identity,
      config: state.config,
      page: state.pageDynamic.map((dyn) => Coms4csState.mergeGrantedProperties(state.pageStatic, dyn)),
    };
  }

  /**
   * Retrieves the initial snapshot from the state
   *
   * @return {ApiLoadConsentResponse | undefined} The first snapshot in the state, or undefined if no snapshots exist
   */
  getInitialSnapshot() {
    const state = this.getState();
    return state?.page[0];
  }

  /**
   * Retrieves the previous snapshot from the state
   *
   * @return {ApiLoadConsentResponse | undefined} The second-to-last snapshot in the state, or undefined if fewer than 2 snapshots exist
   */
  getPreviousSnapshot() {
    const state = this.getState();
    return state.page.length > 1 ? state.page[state.page.length - 2] : undefined;
  }

  /**
   * Retrieves the most recent snapshot from the state
   *
   * @return {ApiLoadConsentResponse | undefined} The last snapshot in the state, or undefined if no snapshots exist
   */
  getLastSnapshot() {
    const state = this.getState();
    return state.page[state.page.length - 1];
  }

  /**
   * Saves the current state of the ComsStore as a new snapshot
   *
   * Adds the current page snapshot to the state and saves it to SessionStorage
   */
  saveSnapshot() {
    const state = this.getState();
    if (state.page.length > Coms4csState.MAX_UNDO_DEEPNESS + 1) {
      // page element cannot have more than 3 items - initial, previous and current
      // (in the case of MAX_UNDO_DEEPNESS = 1)
      state.page.splice(1, state.page.length - Coms4csState.MAX_UNDO_DEEPNESS - 1);
    }
    state.page.push(this.comsStore.getPageSnapshot());
    Coms4csState.setState(state);
  }

  /**
   * Clears the state in SessionStorage
   *
   * Removes all stored snapshots and state information
   */
  reset() {
    Coms4csState.setState();
  }

  /**
   * Removes the specified number of most recent snapshots from the state
   *
   * @param num - The number of snapshots to remove (1 or 2)
   */
  removeLastSnapshots(num: 1 | 2) {
    const state = this.getState();
    if (state.page.length > num - 1) {
      state.page.splice(-num);
      Coms4csState.setState(state);
    }
  }
}
